// src/utils/normalize.ts

/**
 * Apply scroll snapping & section sizing to #scroll-wrapper.
 * Idempotent. If called before sections exist, it will wait and apply once.
 */
import { log } from "./debug";

type AnyEl = HTMLElement & { __scNormalized?: boolean; __scNormObserver?: MutationObserver };

export function normalizeScrollWrapper(): void {
  const wrapper = document.getElementById("scroll-wrapper") as AnyEl | null;
  if (!wrapper) return;

  // Already normalized? bail.
  if (wrapper.__scNormalized) return;

  const applyNow = () => {
    // Mark as normalized so we don't repeat work.
    wrapper.__scNormalized = true;

    // Wrapper-level posture
    wrapper.style.overflowY = "scroll";
    wrapper.style.height = "100vh";
    wrapper.style.scrollBehavior = "smooth";
    (wrapper.style as any).scrollSnapType = "y mandatory";
    (wrapper.style as any).overscrollBehavior = "contain";

    // Per-section normalization
    const sections = wrapper.querySelectorAll<HTMLElement>("section");
    sections.forEach((section, i) => {
      section.style.scrollSnapAlign = "start";
      (section.style as any).scrollSnapStop = "always";
      section.style.minHeight = "100vh";
      section.style.boxSizing = "border-box";
      section.style.margin = "0";

      // Ensure overlays anchor correctly; sections must be positioned containers
      try {
        const cs = window.getComputedStyle(section);
        if (cs.position === "static") section.style.position = "relative";
      } catch {
        if (!section.style.position) section.style.position = "relative";
      }

      // Ensure a stable index attribute for downstream logic
      if (!section.dataset.sectionIndex) section.dataset.sectionIndex = String(i);
      // Keep legacy 'index' if the rest of your code still reads it
      section.dataset.index = section.dataset.index ?? String(i);
    });

    log(`[NORMALIZE] ✅ Applied scroll styles to ${sections.length} section(s).`);

    // Watch for sections added later and normalize them on the fly (idempotent).
    // @ts-ignore - extend wrapper with a private observer reference
    if (!(wrapper as any).__scSectionObserver) {
      const normalizeOne = (section: HTMLElement) => {
        section.style.scrollSnapAlign = "start";
        (section.style as any).scrollSnapStop = "always";
        section.style.minHeight = "100vh";
        section.style.boxSizing = "border-box";
        section.style.margin = "0";
        try {
          const cs = window.getComputedStyle(section);
          if (cs.position === "static") section.style.position = "relative";
        } catch {
          if (!section.style.position) section.style.position = "relative";
        }
        // ensure indices (don’t overwrite if already set)
        if (!section.dataset.sectionIndex) {
          const all = wrapper.querySelectorAll("section");
          all.forEach((s, i) => {
            if (!s.dataset.sectionIndex) s.dataset.sectionIndex = String(i);
            s.dataset.index = s.dataset.index ?? String(i);
          });
        }
      };

      const moNew = new MutationObserver((mutations) => {
        let touched = false;
        for (const m of mutations) {
          m.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement && node.tagName.toLowerCase() === "section") {
              normalizeOne(node as HTMLElement);
              touched = true;
            }
          });
        }
        if (touched) {
          try {
            log("[NORMALIZE] ♻️ Applied scroll styles to newly added section(s).");
          } catch {}
        }
      });

      moNew.observe(wrapper, { childList: true, subtree: true });
      // @ts-ignore store observer so we don't duplicate it
      (wrapper as any).__scSectionObserver = moNew;
    }
  };

  // If sections already exist, apply immediately.
  if (wrapper.querySelector("section")) {
    applyNow();
    return;
  }

  // Otherwise, observe once until a section appears, then apply and stop observing.
  if (wrapper.__scNormObserver) return; // already observing

  const mo = new MutationObserver(() => {
    if (wrapper.querySelector("section")) {
      try { mo.disconnect(); } catch {}
      wrapper.__scNormObserver = undefined;
      applyNow();
    }
  });
  mo.observe(wrapper, { childList: true, subtree: true });
  wrapper.__scNormObserver = mo;
}

/**
 * Legacy hook used to "seed hidden state" for reveals. In the GSAP-only pipeline,
 * we avoid adding Tailwind classes or transforms here (they fight GSAP).
 * We only:
 *  - mark the section as initialized (dataset flag),
 *  - ensure unrevealed elements start invisible (visibility: hidden)
 *    so there is no flash before GSAP runs.
 */
export function initHiddenState(section: HTMLElement): void {
  if (!section) return;
  if (section.dataset.animInit === "1") return;
  section.dataset.animInit = "1";

  const prefersReduced = (() => {
    try {
      return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch {
      return false;
    }
  })();

  // Elements/regions we never want to touch (HUD, captions, etc.)
  const EXCLUDE_ANCESTOR =
    ".hud-reel-controls, .lite-hud-controls, .reel-audio-gate, .pause-overlay, .sc-captions-layer";

  // Self selectors we never want to prime (overlay hosts, etc.)
  const EXCLUDE_SELF = ".section-image";

  // Only true animation targets (declarative); never hide overlay hosts/wrappers.
  const candidates = Array.from(
    section.querySelectorAll<HTMLElement>("[data-anim], .sc-anim")
  )
    // skip controls/overlays we shouldn't touch
    .filter((el) => !el.closest(EXCLUDE_ANCESTOR))
    // skip overlay host wrappers entirely; their child IMG will animate instead
    .filter((el) => !el.matches(EXCLUDE_SELF))
    // skip wrappers/targets explicitly marked to be ignored by animations
    .filter((el) => {
      const v = (el.getAttribute("data-anim-skip") || "").toLowerCase();
      return v !== "1" && v !== "true";
    });

  if (prefersReduced) {
    // In reduced motion, reveal immediately and mark as done to prevent later tweens.
    candidates.forEach((el) => {
      el.dataset.animated = "true";
      el.style.visibility = "visible";
      el.style.opacity = "1";
    });
    return;
  }

  candidates.forEach((el) => {
    const already = (el.dataset.animated || "").toLowerCase() === "true";
    if (!already) {
      // Avoid layout flashes before GSAP presets run.
      // Important: we do not set opacity or transforms here to avoid fighting GSAP.
      el.style.visibility = "hidden";
    }
  });
}